import React, { useEffect, useRef, useState } from "react";
import cn from "classnames";
/*
This is a re-usable select component, meant to be passed to the back end and used in cshtml. Before passing please note:

 - componentName - is important, it will update the ID's and labels so there are no duplicates if this component shows multiple
    times on one page. This will make it easier to pass to BE with the right IDs and labels
 - isScrollTo - This component was initially made for a scroll to selection, leave as false if it is a regular selection and ensure BE does not 
    place the js-select-scrollTo class in the cshtml
 - placeholder - this is optional. If placed, this will be the initial text shown in the input before selecting
 - options - either an array of strings, or array of objects with label and value
 - callback - optional function that fires when selection changes. When provided, uses React state management instead of vanilla JS
*/

const MultiSelect = props => {
  const {
    title,
    options,
    componentName,
    placeholder,
    selectedValues,
    setSelectedValues,
    callback,
  } = props;
  const selectRef = useRef(null);
  // const [selectedValues, setSelectedValues] = useState({});

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    options &&
      options.forEach((option, i) => {
        if (option.isDefault) {
          const value = option.value ?? option;
          const label = option.label ?? option;
          setSelectedValues(prev => ({
            ...prev,
            [value]: { label, index: i },
          }));
        }
      });

    // Handle click outside to close dropdown
    const handleClickOutside = event => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle React-managed selection changes
  const handleOptionClick = (option, index, event) => {
    event.stopPropagation(); // Prevent event bubbling
    const value = option.value ?? option;
    const label = option.label ?? option;
    // Create a new object reference to ensure React state updates
    let newValues = { ...selectedValues };
    if (newValues[value]) {
      delete newValues[value];
    } else {
      newValues[value] = label;
    }

    setSelectedValues(newValues);
    if (callback) callback(newValues);
  };

  // Handle dropdown toggle for React-managed selects
  const handleDropdownToggle = () => {
    if (!isOpen) setIsOpen(true);
  };

  // Handle keyboard navigation for React-managed selects
  const handleKeyDown = event => {
    const { key } = event;

    if (key === "Enter" || key === " ") {
      event.preventDefault();
      setIsOpen(!isOpen);
    } else if (key === "Escape") {
      event.preventDefault();
      setIsOpen(false);
    }
  };

  const optionIsSelected = optionValue => {
    return selectedValues && selectedValues[optionValue] !== undefined;
  };

  return (
    <div
      ref={selectRef}
      className={cn("custom-dropdown", {
        open: isOpen, // Add open class for React-managed selects
      })}
    >
      <div className="sr-only" id={`${componentName}-dropdown-label`}>
        {title ?? "Table of Contents"}
      </div>
      <button
        className="custom-dropdown__input"
        id={`${componentName}-combo`}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-labelledby={`${componentName}-dropdown-label`}
        aria-controls={`${componentName}-dropdown-list`}
        tabIndex="0"
        data-placeholder={placeholder}
        onClick={handleDropdownToggle}
        onKeyDown={handleKeyDown}
        type="button"
      >
        {placeholder}
      </button>
      <div
        className="custom-dropdown__menu"
        id={`${componentName}-dropdown-list`}
        role="listbox"
        tabIndex="-1"
      >
        {options &&
          options.map((option, i) => (
            <React.Fragment key={option.value ?? `option-${i}`}>
              <div
                id={`${componentName}-combo-${i}`}
                role="option"
                className={`custom-dropdown__option ${optionIsSelected(option.value) ? "custom-dropdown__option--active" : ""}`}
                data-value={option.value ?? option}
                data-is-default={option.isDefault}
                onClick={event => handleOptionClick(option, i, event)}
              >
                <span>{option.label ?? option}</span>
              </div>
            </React.Fragment>
          ))}
      </div>
    </div>
  );
};
export default MultiSelect;
